import buildRequestFilter from "./buildRequestFilter";

function buildFrom(current, resultsPerPage) {
  if (!current || !resultsPerPage) return;
  return (current - 1) * resultsPerPage;
}

function buildSort(sortDirection, sortField) {
  if (sortDirection && sortField) {
    console.log('[SORT]', sortDirection, sortField)
    let sortFieldName = sortField + (['published', 'cvss'].indexOf(sortField) >= 0 ? '' : '.keyword')
    return [{ [sortFieldName]: {order:sortDirection} }];
  }
}

function buildMatch(searchTerm) {
  return searchTerm
    ? {
        multi_match: {
          query: searchTerm,
          fields: ["cvelist", "title", "description"],
        },
      }
    : { match_all: {} };
}

/*

  Converts current application state to an Elasticsearch request.

  When implementing an onSearch Handler in Search UI, the handler needs to take the
  current state of the application and convert it to an API request.

  For instance, there is a "current" property in the application state that you receive
  in this handler. The "current" property represents the current page in pagination. This
  method converts our "current" property to Elasticsearch's "from" parameter.

  This "current" property is a "page" offset, while Elasticsearch's "from" parameter
  is a "item" offset. In other words, for a set of 100 results and a page size
  of 10, if our "current" value is "4", then the equivalent Elasticsearch "from" value
  would be "40". This method does that conversion.

  We then do similar things for searchTerm, filters, sort, etc.
*/
export default function buildRequest(state) {
  const {
    current,
    filters,
    resultsPerPage,
    searchTerm,
    sortDirection,
    sortField,
  } = state;

  const sort = buildSort(sortDirection, sortField);
  const match = buildMatch(searchTerm);
  const size = resultsPerPage;
  const from = buildFrom(current, resultsPerPage);
  const filter = buildRequestFilter(filters);

  const body = {
    // Static query Configuration
    // --------------------------
    // https://www.elastic.co/guide/en/elasticsearch/reference/7.x/search-request-highlighting.html
    highlight: {
      fragment_size: 200,
      number_of_fragments: 1,
      fields: {
        cvelist: {},
        title: {},
        description: {},
      },
    },
    //https://www.elastic.co/guide/en/elasticsearch/reference/7.x/search-request-source-filtering.html#search-request-source-filtering
    _source: [
      "id",
      "cvelist",
      "title",
      "description",
      "type",
      "href",
      "modified",
      "score",
    ],
    aggs: {
      type: { terms: { field: "type.keyword" } },
      bulletinFamily: { terms: { field: "bulletinFamily.keyword" } },
      vulnerabilityType: { terms: { field: "vulnerabilityType.keyword" } },
      score: {
        range: {
          field: "score",
          ranges: [
            { from: 0, to: 1, key: "0 - 1" },
            { from: 1, to: 2, key: "1 - 2" },
            { from: 2, to: 3, key: "2 - 3" },
            { from: 3, to: 4, key: "3 - 4" },
            { from: 4, to: 5, key: "4 - 5" },
            { from: 5, to: 6, key: "5 - 6" },
            { from: 6, to: 7, key: "6 - 7" },
            { from: 7, to: 8, key: "7 - 8" },
            { from: 8, to: 9, key: "8 - 9" },
            { from: 9, to: 10, key: "9 - 10" },
          ],
        },
      },
    },

    // Dynamic values based on current Search UI state
    // --------------------------
    // https://www.elastic.co/guide/en/elasticsearch/reference/7.x/full-text-queries.html
    query: {
      bool: {
        must: [match],
        ...(filter && { filter }),
      },
    },
    // https://www.elastic.co/guide/en/elasticsearch/reference/7.x/search-request-sort.html
    ...(sort && { sort }),
    // https://www.elastic.co/guide/en/elasticsearch/reference/7.x/search-request-from-size.html
    ...(size && { size }),
    ...(from && { from }),
  };

  return body;
}
